import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Empty, Image, Select, Skeleton, Spin, Table, Tag } from "antd";
import { PictureOutlined } from "@ant-design/icons";
import { RxMagicWand } from "react-icons/rx";
import PageHeader from "../../../components/pageheader/PageHeader";
import {
  getProductSimilarArticles,
  getSkusFiltered,
} from "../../../api/Services";
import {
  useDebouncedValue,
  useFetchRequestGuard,
} from "../../../helpers/FilterSearchHelper";
import type {
  ProductSimilarArticleItem,
  ProductSimilarArticles,
} from "../../../types/productAnalysis";
import styles from "./SimilarArticles.module.scss";

interface SkuOption {
  id: number;
  externalId: string | null;
  name: string;
}

const fmtMoney = (v: number | null) =>
  v == null
    ? "-"
    : `$${v.toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;
const fmtPct = (v: number | null) =>
  v == null ? "-" : `${v.toLocaleString("es-AR", { maximumFractionDigits: 1 })}%`;
const fmtN = (v: number | null) => (v ?? 0).toLocaleString("es-AR");

const skuLabel = (sku: SkuOption) =>
  sku.externalId ? `${sku.externalId} - ${sku.name}` : sku.name;

const ArticlePhoto = ({ src, size }: { src: string | null; size: number }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [src]);

  return src && !failed ? (
    <Image
      src={src}
      width={size}
      height={size}
      className={styles.photo}
      preview={{ mask: false }}
      onError={() => setFailed(true)}
    />
  ) : (
    <div className={styles.noPhoto} style={{ width: size, height: size }}>
      <PictureOutlined />
    </div>
  );
};

const SimilarArticles = () => {
  const { t } = useTranslation();
  const [skuSearchValue, setSkuSearchValue] = useState("");
  const debouncedSkuSearchValue = useDebouncedValue(skuSearchValue);
  const [skuOptions, setSkuOptions] = useState<SkuOption[]>([]);
  const [skuOptionsLoading, setSkuOptionsLoading] = useState(false);
  const [selectedSku, setSelectedSku] = useState<SkuOption>();
  const [result, setResult] = useState<ProductSimilarArticles | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const { beginRequest: beginSkuSearch, isStaleRequest: isStaleSkuSearch } =
    useFetchRequestGuard();
  const { beginRequest: beginSimilar, isStaleRequest: isStaleSimilar } =
    useFetchRequestGuard();

  useEffect(() => {
    const value = debouncedSkuSearchValue.trim();
    const requestId = beginSkuSearch();
    if (!value) {
      setSkuOptions([]);
      setSkuOptionsLoading(false);
      return;
    }
    setSkuOptionsLoading(true);
    Promise.all([
      getSkusFiltered(0, { externalId: value }),
      getSkusFiltered(0, { skuName: value }),
    ]).then(([byExternalId, byName]) => {
      if (isStaleSkuSearch(requestId)) return;
      const merged = new Map<number, SkuOption>();
      [...byExternalId.skus, ...byName.skus].forEach((sku: SkuOption) =>
        merged.set(sku.id, sku)
      );
      setSkuOptions(Array.from(merged.values()));
      setSkuOptionsLoading(false);
    });
  }, [debouncedSkuSearchValue, beginSkuSearch, isStaleSkuSearch]);

  useEffect(() => {
    const requestId = beginSimilar();
    if (!selectedSku) {
      setResult(null);
      setError(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    getProductSimilarArticles(selectedSku.id).then((response) => {
      if (isStaleSimilar(requestId)) return;
      setResult(response);
      setError(response === null);
      setLoading(false);
    });
  }, [selectedSku, beginSimilar, isStaleSimilar]);

  const selectOptions = useMemo(() => {
    const options =
      selectedSku && !skuOptions.some((sku) => sku.id === selectedSku.id)
        ? [selectedSku, ...skuOptions]
        : skuOptions;
    return options.map((sku) => ({ value: sku.id, label: skuLabel(sku) }));
  }, [selectedSku, skuOptions]);

  const columns = [
    {
      title: t("SimilarArticles-Column-Image"),
      dataIndex: "photo",
      key: "photo",
      width: 72,
      render: (photo: string | null) => <ArticlePhoto src={photo} size={48} />,
    },
    {
      title: t("Common-Sku"),
      dataIndex: "skuExternalId",
      key: "skuExternalId",
      render: (v: string | null) => v ?? "-",
    },
    {
      title: t("Common-Description"),
      dataIndex: "name",
      key: "name",
      ellipsis: true,
    },
    {
      title: t("Common-Category"),
      dataIndex: "categoryName",
      key: "categoryName",
      ellipsis: true,
      responsive: ["md" as const],
      render: (v: string | null) => v ?? "-",
    },
    {
      title: t("Common-Brand"),
      dataIndex: "brandName",
      key: "brandName",
      ellipsis: true,
      responsive: ["md" as const],
      render: (v: string | null) => v ?? "-",
    },
    {
      title: t("Common-Price"),
      dataIndex: "price",
      key: "price",
      align: "right" as const,
      render: fmtMoney,
    },
    {
      title: t("Common-Cost"),
      dataIndex: "cost",
      key: "cost",
      align: "right" as const,
      responsive: ["lg" as const],
      render: fmtMoney,
    },
    {
      title: t("Common-Margin"),
      dataIndex: "marginPct",
      key: "marginPct",
      align: "right" as const,
      responsive: ["lg" as const],
      render: fmtPct,
    },
    {
      title: t("Common-Stock"),
      dataIndex: "stock",
      key: "stock",
      align: "right" as const,
      render: fmtN,
    },
    {
      title: t("SimilarArticles-Column-Sales"),
      dataIndex: "units",
      key: "units",
      align: "right" as const,
      render: fmtN,
    },
    {
      title: t("ProductAnalysis-Column-Similarity"),
      dataIndex: "similarityPct",
      key: "similarityPct",
      align: "right" as const,
      render: (v: number | null) => (v == null ? "-" : `${v.toFixed(0)}%`),
    },
    {
      title: t("ProductAnalysis-Column-Reason"),
      dataIndex: "reason",
      key: "reason",
      responsive: ["lg" as const],
      render: (v: string | null) => (v ? <Tag>{v}</Tag> : "-"),
    },
  ];

  const renderOriginal = (original: ProductSimilarArticleItem) => (
    <div className={styles.originalItemBox}>
      <ArticlePhoto src={original.photo} size={80} />
      <div className={styles.originalDetails}>
        <div className={styles.originalTitle}>
          {t("SimilarArticles-OriginalArticle")}
        </div>
        <div>
          <b>{t("Common-Description")}:</b> {original.name}
        </div>
        <div>
          <b>{t("Common-Sku")}:</b> {original.skuExternalId ?? "-"}
        </div>
        <div>
          <b>{t("Common-Category")}:</b> {original.categoryName ?? "-"}
        </div>
        <div>
          <b>{t("Common-Brand")}:</b> {original.brandName ?? "-"}
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    if (!selectedSku) {
      return <Empty description={t("SimilarArticles-SelectSku")} />;
    }
    if (loading) {
      return (
        <>
          <div className={styles.originalItemBox}>
            <Skeleton.Avatar active shape="square" size={80} />
            <Skeleton active paragraph={{ rows: 3 }} />
          </div>
          <Skeleton active paragraph={{ rows: 6 }} />
        </>
      );
    }
    if (error || !result) {
      return <Alert type="error" showIcon message={t("SimilarArticles-Error")} />;
    }
    return (
      <>
        {renderOriginal(result.original)}
        {!result.enabled ? (
          <Alert type="info" showIcon message={t("SimilarArticles-Disabled")} />
        ) : (
          <div className={styles.similarItemsBox}>
            <h3>{t("NavBar-SimilarArticles")}</h3>
            <Table
              rowKey="skuId"
              columns={columns}
              dataSource={result.items}
              pagination={false}
              scroll={{ x: 900 }}
              locale={{ emptyText: t("SimilarArticles-EmptyResults") }}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={t("NavBar-SimilarArticles")}
        icon={<RxMagicWand />}
        showCreateButton={false}
      />
      <div className={styles.contentWrapper}>
        <div className={styles.tableWrapper}>
          <div className={styles.selectorBox}>
            <Select
              showSearch
              allowClear
              className={styles.skuSelect}
              placeholder={t("SimilarArticles-SearchSkuPlaceholder")}
              defaultActiveFirstOption={false}
              filterOption={false}
              showArrow={false}
              searchValue={skuSearchValue}
              onSearch={setSkuSearchValue}
              notFoundContent={skuOptionsLoading ? <Spin size="small" /> : null}
              options={selectOptions}
              value={selectedSku?.id}
              onChange={(value?: number) => {
                setSelectedSku(
                  skuOptions.find((sku) => sku.id === value) ??
                    (value === selectedSku?.id ? selectedSku : undefined)
                );
                setSkuSearchValue("");
              }}
            />
          </div>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default SimilarArticles;
